/**
 * AudioManager — Howler.js wrapper for game audio
 *
 * Handles:
 * - SFX playback with pooling (multiple simultaneous instances)
 * - Background music with crossfade
 * - Mobile unlock (iOS requires user gesture before audio plays)
 * - Volume control per category (master, sfx, music)
 * - Procedural SFX via Web Audio API oscillator (fallback)
 */

// Howler import — gracefully degrades to silence if not available
let Howl = null;
try {
  // eslint-disable-next-line
  const howler = require('howler');
  Howl = howler.Howl;
} catch (e) {
  // Howler not available — procedural SFX via Web Audio API still works
}

export class AudioManager {
  constructor() {
    this._sounds = {};      // id → Howl instance
    this._music = null;     // current music Howl
    this._musicId = null;
    this._unlocked = false;
    this._muted = false;

    this.masterVolume = 0.7;
    this.sfxVolume = 0.8;
    this.musicVolume = 0.4;

    // Procedural SFX via Web Audio API
    this._audioCtx = null;

    // Mobile unlock listener
    this._unlockHandler = () => this._unlock();
    document.addEventListener('touchstart', this._unlockHandler, { once: true });
    document.addEventListener('click', this._unlockHandler, { once: true });
  }

  _unlock() {
    this._unlocked = true;
    // Initialize Web Audio API context
    try {
      this._audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      if (this._audioCtx.state === 'suspended') {
        this._audioCtx.resume();
      }
    } catch (e) {
      // No Web Audio API available
    }
  }

  // --- Sound Registration ---

  /** Register a sound effect from a URL or data URI */
  register(id, src, options = {}) {
    if (!Howl) return;

    const { volume = 1, loop = false, pool = 5 } = options;

    this._sounds[id] = new Howl({
      src: [src],
      volume: volume * this.sfxVolume * this.masterVolume,
      loop,
      pool,
      preload: true,
    });
  }

  /** Register background music */
  registerMusic(id, src) {
    if (!Howl) return;

    this._sounds[id] = new Howl({
      src: [src],
      volume: this.musicVolume * this.masterVolume,
      loop: true,
      preload: true,
    });
  }

  // --- Playback ---

  play(id) {
    if (this._muted) return;
    const sound = this._sounds[id];
    if (sound) sound.play();
    return this;
  }

  playMusic(id) {
    if (this._musicId === id) return; // already playing

    // Stop current music
    if (this._music) {
      this._music.fade(this._music.volume(), 0, 500);
      setTimeout(() => this._music && this._music.stop(), 500);
    }

    const sound = this._sounds[id];
    if (sound) {
      this._music = sound;
      this._musicId = id;
      sound.play();
      sound.fade(0, this.musicVolume * this.masterVolume, 1000);
    }
  }

  stopMusic() {
    if (this._music) {
      this._music.fade(this._music.volume(), 0, 500);
      setTimeout(() => {
        if (this._music) this._music.stop();
        this._music = null;
        this._musicId = null;
      }, 500);
    }
  }

  // --- Procedural SFX (no external files needed) ---

  /** Quick oscillator-based sound effect */
  synthSfx(type = 'shoot') {
    if (this._muted || !this._audioCtx) return;

    const ctx = this._audioCtx;
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);

    const osc = ctx.createOscillator();
    osc.connect(gain);

    switch (type) {
      case 'shoot':
        osc.type = 'square';
        osc.frequency.setValueAtTime(800, now);
        osc.frequency.exponentialRampToValueAtTime(200, now + 0.08);
        gain.gain.setValueAtTime(0.15 * this.sfxVolume, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
        osc.start(now);
        osc.stop(now + 0.08);
        break;

      case 'hit':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.exponentialRampToValueAtTime(100, now + 0.05);
        gain.gain.setValueAtTime(0.2 * this.sfxVolume, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
        osc.start(now);
        osc.stop(now + 0.06);
        break;

      case 'explode':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(150, now);
        osc.frequency.exponentialRampToValueAtTime(30, now + 0.3);
        gain.gain.setValueAtTime(0.3 * this.sfxVolume, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
        osc.start(now);
        osc.stop(now + 0.3);
        // Add noise layer
        const noise = ctx.createOscillator();
        noise.type = 'triangle';
        noise.frequency.setValueAtTime(80, now);
        noise.frequency.exponentialRampToValueAtTime(20, now + 0.2);
        const ng = ctx.createGain();
        ng.gain.setValueAtTime(0.2 * this.sfxVolume, now);
        ng.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
        noise.connect(ng);
        ng.connect(ctx.destination);
        noise.start(now);
        noise.stop(now + 0.25);
        break;

      case 'pickup':
        osc.type = 'sine';
        osc.frequency.setValueAtTime(600, now);
        osc.frequency.exponentialRampToValueAtTime(1200, now + 0.1);
        gain.gain.setValueAtTime(0.15 * this.sfxVolume, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
        osc.start(now);
        osc.stop(now + 0.12);
        break;

      case 'levelup':
        osc.type = 'sine';
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.linearRampToValueAtTime(800, now + 0.15);
        osc.frequency.linearRampToValueAtTime(1200, now + 0.3);
        gain.gain.setValueAtTime(0.2 * this.sfxVolume, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
        osc.start(now);
        osc.stop(now + 0.4);
        break;

      case 'death':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(300, now);
        osc.frequency.exponentialRampToValueAtTime(50, now + 0.8);
        gain.gain.setValueAtTime(0.25 * this.sfxVolume, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
        osc.start(now);
        osc.stop(now + 0.8);
        break;

      case 'combo':
        osc.type = 'sine';
        osc.frequency.setValueAtTime(500, now);
        osc.frequency.exponentialRampToValueAtTime(900, now + 0.06);
        gain.gain.setValueAtTime(0.12 * this.sfxVolume, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
        osc.start(now);
        osc.stop(now + 0.08);
        break;

      default:
        osc.stop(now);
    }
  }

  // --- Controls ---

  mute() {
    this._muted = true;
    if (this._music) this._music.mute(true);
  }

  unmute() {
    this._muted = false;
    if (this._music) this._music.mute(false);
  }

  toggleMute() {
    if (this._muted) this.unmute();
    else this.mute();
    return this._muted;
  }

  destroy() {
    document.removeEventListener('touchstart', this._unlockHandler);
    document.removeEventListener('click', this._unlockHandler);
    for (const id in this._sounds) {
      this._sounds[id].unload();
    }
    this._sounds = {};
    if (this._audioCtx) this._audioCtx.close();
  }
}
